import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Input } from "@/shared/ui/input";
import { Loader2 } from "lucide-react";
import { useCreateAddress, useUpdateAddress } from "@/domains/addresses/services/addresses";
import { CreateAddressRequest, UpdateAddressRequest } from "@/shared/types/addressTypes";
import { Address } from "@/shared/types/addressTypes";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import { AddressMapSelector, SearchResult } from "@/domains/addresses/components/AddressMapSelector";

interface AddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  address?: Address;
  selectedRegionCode: string;
}

const AddressDialog = ({
  open,
  onOpenChange,
  onSuccess,
  address,
  selectedRegionCode
}: AddressDialogProps) => {
  const [formData, setFormData] = useState<Partial<CreateAddressRequest | UpdateAddressRequest> | null>(null);
  const [nickname, setNickname] = useState('');
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({ lat: 64.1355, lng: -21.8954 }); // Default to Reykjavik, Iceland

  const createAddress = useCreateAddress(selectedRegionCode);
  const updateAddress = useUpdateAddress();

  useEffect(() => {
    if (open) {
      if (address) {
        setNickname(address.metadata);
        setFormData(address);
        setMapCenter({ lat: address.latitude, lng: address.longitude });
      } else {
        setNickname('');
        setFormData(null);
        setMapCenter({ lat: 64.1355, lng: -21.8954 }); // Default to Reykjavik, Iceland
      }
    }
  }, [open, address]);

  // Handle address selection from map
  const handleAddressSelect = (address: SearchResult) => {
    // Ensure all required address fields are properly set
    const addressData = {
      formattedAddress: address.formattedAddress,
      latitude: address.latitude,
      longitude: address.longitude,
      // Include parsed address components
      locality: address.locality || '',
      administrativeArea: address.administrativeArea || '',
      administrativeAreaCode: address.administrativeAreaCode || '',
      postalCode: address.postalCode || '',
      regionCode: address.regionCode || '',
      region: address.region || '',
      addressLines: address.addressLines || [address.formattedAddress],
    };

    setFormData(prev => ({
      ...prev,
      ...addressData,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData?.latitude === null || formData?.longitude === null) {
      showError(ERROR.ADDRESS.SELECT_LOCATION_ON_MAP);
      return;
    }

    if (!formData?.formattedAddress) {
      showError(ERROR.ADDRESS.ENTER_ADDRESS);
      return;
    }

    if (address) {
      handleUpdateAddress(formData as UpdateAddressRequest, nickname, address);
    } else {
      handleCreateAddress(formData as CreateAddressRequest, nickname);
    }
  };

  const handleCreateAddress = async (formData: CreateAddressRequest, nickname: string) => {
    try {
      const requestPayload = {
        ...formData,
        metadata: nickname,
      } as CreateAddressRequest;

      await createAddress.mutateAsync(requestPayload);
      showSuccess(SUCCESS.ADDRESS.CREATED);
      onSuccess();
    } catch (error) {
      showErrorWithApiError(error, ERROR.ADDRESS.CREATE);
    } finally {
      handleDialogClose(false);
    }
  }

  const handleUpdateAddress = async (formData: UpdateAddressRequest, nickname: string, address: Address) => {
    try {
      await updateAddress.mutateAsync(
        {
          addressId: address.addressReferenceId,
          data: {
            ...formData,
            metadata: nickname,
          } as UpdateAddressRequest,
        });
      showSuccess(SUCCESS.ADDRESS.UPDATED);
      onSuccess();
    } catch (error) {
      showErrorWithApiError(error, ERROR.ADDRESS.UPDATE);
    } finally {
      handleDialogClose(false);
    }
  }

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setFormData(null);
      setNickname('');
      setMapCenter({ lat: 64.1355, lng: -21.8954 }); // Default to Reykjavik, Iceland
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[600px] z-[100]">
        <DialogHeader>
          <DialogTitle>{address ? 'Edit Address' : 'Add New Address'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Address Selection */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Select Location on Map</p>
            <AddressMapSelector
              onAddressSelect={handleAddressSelect}
              selectedAddress={formData?.latitude && formData?.longitude ? {
                formattedAddress: formData?.formattedAddress || '',
                latitude: formData?.latitude,
                longitude: formData?.longitude,
                placeId: '', // placeId is not part of AddressData interface
                locality: formData?.locality || '',
                administrativeArea: formData?.administrativeArea || '',
                administrativeAreaCode: formData?.administrativeAreaCode || '',
                postalCode: formData?.postalCode || '',
                regionCode: formData?.regionCode || '',
                region: formData?.region || '',
                addressLines: formData?.addressLines || [formData?.formattedAddress || ''],
              } : null}
              mapCenter={mapCenter}
              onMapCenterChange={setMapCenter}
              placeholder="Search for an address..."
            />
          </div>

          {/* Address Nickname - Only show when address is selected */}
          {formData?.latitude !== null && formData?.longitude !== null && (
            <div className="space-y-2">
              <Label htmlFor="nickname">Address Nickname</Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Enter address nickname..."
                className="w-full"
                required={true}
              />
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => handleDialogClose(false)}
              disabled={createAddress.isPending || updateAddress.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createAddress.isPending || updateAddress.isPending || !formData?.formattedAddress || !formData?.latitude || !formData?.longitude || !nickname}
              className="gap-2">
              {createAddress.isPending || updateAddress.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {address ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                address ? 'Update Address' : 'Add Address'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddressDialog; 